import React, { useEffect, useCallback, useState, useRef, lazy, Suspense } from "react";
import { X, Download, ZoomIn, ZoomOut, RotateCw, FileText, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import ZoomableImage from "./ZoomableImage";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-26 (Phase 5); FileViewerModal.scss deleted.
//
// 🔴 Two literals that were doing real work, both in the same eight lines:
//
//   · the close button's hover was `hsl(0 84% 45%)` — a hand-written RED with no
//     token behind it, so the one destructive-looking affordance in the viewer
//     could not follow the palette in either theme. `--danger` now.
//   · the download button was `hsl(var(--primary, 217 91% 48%))` — the brand
//     token with a v1-BLUE fallback baked in. The fallback was dead (the token
//     is declared), but it is the kind of dead that reads as intent.
//
// Everything else was `var(--x, #hex)` pairs where the hex could never render.
//
// The scrim was `rgba(0, 0, 0, 0.7)`; it is `--overlay-bg` now, the same token
// AppModal and FilterPanel use, so all three modal backdrops finally agree and
// all three deepen together in dark mode.
//
// ⚠️ Radii: the sheet wrote `var(--radius-lg, 0.75rem)` and `var(--radius-sm,
// 6px)`. Both fallbacks are WRONG about the token they guard — `--radius-lg` is
// 1rem and `--radius-sm` is 0.5rem — so `tw-rounded-lg` / `tw-rounded-sm` match
// what actually rendered, not what the fallback claimed.
//
// `fvFadeIn` is `panel-fade` and `fvSlideIn` is `dialog-in`; both already in the
// config, neither materially different from what was written here.

const OVERLAY =
  "tw-fixed tw-inset-0 tw-z-modal tw-flex tw-animate-panel-fade tw-items-center " +
  "tw-justify-center tw-bg-[var(--overlay-bg)] tw-backdrop-blur-[4px]";
const MODAL =
  "tw-relative tw-flex tw-h-[90vh] tw-max-h-[90vh] tw-w-[95vw] tw-max-w-[1100px] " +
  "tw-animate-dialog-in tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border " +
  "tw-border-solid tw-border-line tw-bg-surface tw-shadow-popover " +
  "max-[768px]:tw-h-screen max-[768px]:tw-max-h-screen max-[768px]:tw-w-screen " +
  "max-[768px]:tw-max-w-[100vw] max-[768px]:tw-rounded-none";

const HEADER =
  "tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-0 tw-border-b " +
  "tw-border-solid tw-border-line-soft tw-bg-surface-raised tw-px-4 tw-py-3";
const FILENAME =
  "tw-mr-3 tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-text-content";
const TOOLBAR = "tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-overflow-hidden";
const PAGE_LABEL =
  "tw-min-w-[20px] tw-select-none tw-text-center tw-text-xs tw-font-semibold tw-text-content";
const DIVIDER =
  "tw-mx-0.5 tw-inline-block tw-h-[18px] tw-w-px tw-shrink-0 tw-bg-line-soft";
const TOOL_BTN =
  "tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center " +
  "tw-rounded-sm tw-border-none tw-bg-transparent tw-text-content-muted " +
  "tw-transition-[background-color,color] tw-duration-150 " +
  "hover:tw-bg-surface-hover hover:tw-text-content " +
  "disabled:tw-pointer-events-none disabled:tw-cursor-not-allowed disabled:tw-opacity-[0.35]";
const TOOL_BTN_ACTIVE = TOOL_BTN + " tw-bg-surface-hover tw-text-content";
const MORE_WRAP = "tw-relative tw-shrink-0";
const MORE_MENU =
  "tw-absolute tw-right-0 tw-top-[calc(100%+4px)] tw-z-10 tw-min-w-[150px] " +
  "tw-animate-panel-fade tw-rounded-sm tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface tw-p-1 tw-shadow-popover";
const MORE_ITEM =
  "tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap " +
  "tw-rounded-[4px] tw-border-none tw-bg-transparent tw-px-2.5 tw-py-2 tw-text-[13px] " +
  "tw-font-medium tw-text-content tw-transition-colors tw-duration-100 " +
  "hover:tw-bg-surface-hover";
const CLOSE_BTN =
  "tw-ml-1 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center " +
  "tw-rounded-sm tw-border-none tw-bg-transparent tw-text-content-muted " +
  "tw-transition-[background-color,color] tw-duration-150 " +
  "hover:tw-bg-danger/10 hover:tw-text-danger";

const BODY = "tw-flex tw-flex-1 tw-items-center tw-justify-center tw-overflow-auto tw-bg-surface-alt";
const PDF_WRAP = "tw-h-full tw-w-full tw-origin-[center_top]";
const PDF = "tw-h-full tw-w-full tw-border-none";
const TEXT_WRAP = "tw-h-full tw-w-full tw-overflow-auto tw-p-5";
const TEXT =
  "tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-font-mono tw-text-[13px] " +
  "tw-leading-[1.6] tw-text-content";
const OFFICE =
  "tw-flex tw-h-full tw-w-full tw-flex-col tw-self-stretch tw-overflow-hidden tw-bg-surface";
const LOADING = "tw-text-sm tw-text-content-subtle";
const UNSUPPORTED =
  "tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-p-8 tw-text-center " +
  "tw-text-content-subtle";
const UNSUPPORTED_NAME =
  "tw-m-0 tw-break-all tw-text-[15px] tw-font-semibold tw-text-content";
const UNSUPPORTED_HINT = "tw-m-0 tw-text-[13px] tw-text-content-muted";
const DOWNLOAD_BTN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-sm tw-border-none " +
  "tw-bg-primary-solid tw-px-5 tw-py-2 tw-text-[13px] tw-font-medium tw-text-primary-solid-fg " +
  "tw-transition-opacity tw-duration-150 hover:tw-opacity-90";

// Office documents (Word / PowerPoint / Excel) render inline via the shared
// ContentViewer (docx-preview / pptx / Office embed). Lazy-loaded so its heavy
// renderers only ship to the browser when an office file is actually opened.
const ContentViewer = lazy(() => import("../SelfPaced/ContentViewer"));
// A CSV renders as a table, parsed by a few lines that cannot run anything —
// never Office Online (which takes no CSV) and never SheetJS.
const CsvTable = lazy(() => import("../SelfPaced/CsvTable"));

const VIEWABLE_IMAGES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"];
const VIEWABLE_PDF = ["application/pdf"];
const VIEWABLE_TEXT = ["text/plain"];
const OFFICE_EXTENSIONS = ["doc", "docx", "ppt", "pptx", "xls", "xlsx"];
const OFFICE_MIME = /(msword|wordprocessingml|ms-powerpoint|presentationml|ms-excel|spreadsheetml|officedocument)/i;

const getExtension = (s = "") => {
  const clean = String(s).split("?")[0].split("#")[0];
  const dot = clean.lastIndexOf(".");
  return dot >= 0 ? clean.slice(dot + 1).toLowerCase() : "";
};

/** By name first: a browser may have called a .csv text/plain or an Excel type. */
const isCsvFile = (fileType, fileName, fileUrl) =>
  (getExtension(fileName) || getExtension(fileUrl)) === "csv" || /(^|\/)(x-)?csv\b/i.test(fileType || "");

const isOfficeDoc = (fileType, fileName, fileUrl) => {
  const ext = getExtension(fileName) || getExtension(fileUrl);
  return OFFICE_EXTENSIONS.includes(ext) || OFFICE_MIME.test(fileType || "");
};

const isViewable = (type, fileName, fileUrl) =>
  isCsvFile(type, fileName, fileUrl) ||
  VIEWABLE_IMAGES.some((t) => type?.includes(t)) ||
  VIEWABLE_PDF.some((t) => type?.includes(t)) ||
  VIEWABLE_TEXT.some((t) => type?.includes(t)) ||
  isOfficeDoc(type, fileName, fileUrl);

/**
 * FileViewerModal — In-app file viewer overlay.
 * Renders images, PDFs, and text files inline. Non-viewable files get a download prompt.
 *
 * Props:
 *   show      — visibility flag
 *   onHide    — close handler
 *   fileUrl   — URL of the file
 *   fileName  — display name
 *   fileType  — MIME type
 *   downloadUrl — optional; a link that saves under the file's own name
 */
const FileViewerModal = ({ show, onHide, fileUrl, fileName, fileType, downloadUrl }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [textContent, setTextContent] = useState("");
  const [textLoading, setTextLoading] = useState(false);
  const [pdfPage, setPdfPage] = useState(1);
  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef(null);
  const pdfIframeRef = useRef(null);

  const isImage = VIEWABLE_IMAGES.some((t) => fileType?.includes(t));
  const isPdf = VIEWABLE_PDF.some((t) => fileType?.includes(t));
  const isCsv = !isImage && !isPdf && isCsvFile(fileType, fileName, fileUrl);
  const isText = !isCsv && VIEWABLE_TEXT.some((t) => fileType?.includes(t));
  const isOffice = !isImage && !isPdf && !isText && !isCsv && isOfficeDoc(fileType, fileName, fileUrl);
  const officeExt = (getExtension(fileName) || getExtension(fileUrl)).toUpperCase();

  // Reset state when file changes
  useEffect(() => {
    setZoom(1);
    setRotation(0);
    setTextContent("");
    setPdfPage(1);
    setShowMore(false);
  }, [fileUrl]);

  // Navigate PDF to page without full iframe reload (hash navigation)
  useEffect(() => {
    if (!pdfIframeRef.current || !fileUrl) return;
    const base = fileUrl.split("#")[0];
    pdfIframeRef.current.src = `${base}#toolbar=0&navpanes=0&page=${pdfPage}`;
  }, [pdfPage, fileUrl]);

  // Close more menu on outside click
  useEffect(() => {
    if (!showMore) return;
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMore]);

  // Load text file content
  useEffect(() => {
    if (!show || !isText || !fileUrl) return;
    let cancelled = false;
    setTextLoading(true);
    fetch(fileUrl)
      .then((res) => res.text())
      .then((text) => {
        if (!cancelled) setTextContent(text);
      })
      .catch(() => {
        if (!cancelled) setTextContent("Failed to load file content.");
      })
      .finally(() => {
        if (!cancelled) setTextLoading(false);
      });
    return () => { cancelled = true; };
  }, [show, isText, fileUrl]);

  // Escape to close (close more menu first)
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (showMore) { setShowMore(false); return; }
        onHide();
      }
    },
    [onHide, showMore]
  );

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [show, handleKeyDown]);

  if (!show || !fileUrl) return null;

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = downloadUrl || fileUrl;
    a.download = fileName || "download";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className={OVERLAY} onClick={onHide}>
      <div className={MODAL} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* Header */}
        <div className={HEADER}>
          <span className={FILENAME} title={fileName}>{fileName}</span>
          <div className={TOOLBAR}>
            {/* PDF page navigation */}
            {isPdf && (
              <>
                <button
                  className={TOOL_BTN}
                  onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
                  title="Previous page"
                  aria-label="Previous page"
                  disabled={pdfPage <= 1}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={PAGE_LABEL}>{pdfPage}</span>
                <button
                  className={TOOL_BTN}
                  onClick={() => setPdfPage((p) => p + 1)}
                  title="Next page"
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
                <span className={DIVIDER} aria-hidden="true" />
              </>
            )}

            {/* Zoom — PDFs. An image carries its own controls (ZoomableImage). */}
            {isPdf && (
              <>
                <button className={TOOL_BTN} onClick={() => setZoom((z) => Math.min(z + 0.25, 3))} title="Zoom in" aria-label="Zoom in">
                  <ZoomIn size={16} />
                </button>
                <button className={TOOL_BTN} onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))} title="Zoom out" aria-label="Zoom out">
                  <ZoomOut size={16} />
                </button>
              </>
            )}

            <button className={TOOL_BTN} onClick={handleDownload} title="Download" aria-label={`Download ${fileName || "file"}`}>
              <Download size={16} />
            </button>

            {/* More menu — Rotate (keeps toolbar compact on mobile) */}
            {isPdf && (
              <div className={MORE_WRAP} ref={moreRef}>
                <button
                  className={showMore ? TOOL_BTN_ACTIVE : TOOL_BTN}
                  onClick={() => setShowMore((v) => !v)}
                  title="More options"
                  aria-label="More options"
                  aria-expanded={showMore}
                >
                  <MoreHorizontal size={16} />
                </button>
                {showMore && (
                  <div className={MORE_MENU}>
                    <button
                      className={MORE_ITEM}
                      onClick={() => { setRotation((r) => (r + 90) % 360); setShowMore(false); }}
                    >
                      <RotateCw size={14} /> Rotate 90°
                    </button>
                  </div>
                )}
              </div>
            )}
            <button className={CLOSE_BTN} onClick={onHide} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={BODY}>
          {/* A scaled <img> in a scrolling box could not be panned to its
              edges; ZoomableImage zooms about the cursor and drags. The dialog
              is already the whole screen, so plain scroll zooms here. */}
          {isImage && (
            <ZoomableImage
              src={fileUrl}
              alt={fileName || "Image"}
              wheel="always"
              bare
              className="tw-h-full tw-w-full"
            />
          )}

          {isPdf && (
            <div
              className={PDF_WRAP}
              style={{
                zoom: zoom,
                transform: rotation ? `rotate(${rotation}deg)` : undefined,
                transformOrigin: "center top",
              }}
            >
              <iframe
                ref={pdfIframeRef}
                src={`${fileUrl.split("#")[0]}#toolbar=0&navpanes=0`}
                title={fileName}
                className={PDF}
              />
            </div>
          )}

          {isText && (
            <div className={TEXT_WRAP}>
              {textLoading ? (
                <div className={LOADING}>Loading...</div>
              ) : (
                <pre className={TEXT}>{textContent}</pre>
              )}
            </div>
          )}

          {isCsv && (
            <div className={OFFICE}>
              <Suspense fallback={<div className={LOADING}>Loading table…</div>}>
                <CsvTable url={fileUrl} />
              </Suspense>
            </div>
          )}

          {isOffice && (
            <div className={OFFICE}>
              <Suspense fallback={<div className={LOADING}>Loading document…</div>}>
                <ContentViewer
                  fileType={officeExt || undefined}
                  url={fileUrl}
                  title={fileName}
                  fileName={fileName}
                />
              </Suspense>
            </div>
          )}

          {!isImage && !isPdf && !isText && !isCsv && !isOffice && (
            <div className={UNSUPPORTED}>
              <FileText size={48} />
              <p className={UNSUPPORTED_NAME}>{fileName}</p>
              <p className={UNSUPPORTED_HINT}>
                This file type cannot be previewed. Click download to view it.
              </p>
              <button className={DOWNLOAD_BTN} onClick={handleDownload}>
                <Download size={16} /> Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

FileViewerModal.isViewable = isViewable;

export default FileViewerModal;
